import React from 'react';
import { Button, Space, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { ControlOutlined } from '@ant-design/icons';
import { Retry } from '@components/common';
import kpiStore from '@globalStore/kpiStore';
import { currentKpiIdAtom } from '@src/atoms/panels';
import { useCurrentKpiConfig } from '@src/hooks/useCurrentKpiConfig';
import Gauge from './QOEGauge';
import { supportedMetricsSelector } from '@src/store/common/selectors';
import useFetchQoEData from './useFetchQoEData';
import { useWidget } from '../../state/dashboard';
import Trend from './QOETrend';
import { SPI_METRIC } from '@utils/kpi/spi';
import RemoveWidget from '../../control/RemoveWidget';
import useDiagDashboard from '../../hooks/useDiagDashboard';
import { GaugeWrapper } from './styled';
import Drilldown from '../../control/Drilldown';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import { DEFAULT_METRIC, SPI_QUERY_METRICS } from '../../utils/spi';

export const QoEWidget = ({ wid }) => {
  const widgetHandler = useWidget({ wid });
  const currentKpi = useCurrentKpiConfig();
  const currentKpiID = useAtomValue(currentKpiIdAtom);
  const supportedMetrics: string[] = useSelector(supportedMetricsSelector);
  const allowedMetrics = SPI_QUERY_METRICS.filter((option) => option in supportedMetrics);
  const setIsKpiDrawerOpen = kpiStore((state: any) => state.setIsKpiDrawerOpen);
  const isKpiDrawerOpen = kpiStore((state: any) => state.isKpiDrawerOpen);
  const { isFetching, isError, data, refetch, queued } = useFetchQoEData({
    wid,
    metrics: allowedMetrics,
    currentKpi,
  });
  const totalsData = data?.totals;

  const { createDashboard } = useDiagDashboard({
    metrics: SPI_QUERY_METRICS,
    defaultMetric: DEFAULT_METRIC.spi,
    title: widgetHandler.meta.title,
  });

  const isThresholdServiceDown = currentKpi === '';

  const handleKpiDrawerChange = () => {
    setIsKpiDrawerOpen(!isKpiDrawerOpen);
  };

  return (
    <WidgetCardWrapper
      wid={wid}
      title={widgetHandler.meta.title}
      style={{ gridRow: 'span 7' }}
      extra={
        <Space>
          <Tooltip title="Edit KPI Configuration">
            <Button
              type="text"
              disabled={isThresholdServiceDown}
              onClick={() => handleKpiDrawerChange()}
              icon={<ControlOutlined />}
            />
          </Tooltip>
          <Drilldown createDashboard={createDashboard} />
          <RemoveWidget onClick={widgetHandler.remove} />
        </Space>
      }
    >
      <Retry
        isLoading={isFetching}
        showError={isError}
        fetchData={refetch}
        progress={true}
        queued={queued}
      >
        <div>
          <GaugeWrapper>
            <Gauge key={'spi-guage'} currentKpi={currentKpi} data={totalsData} />
            <Trend metricKey={SPI_METRIC} data={data?.data[SPI_METRIC]} />
          </GaugeWrapper>
        </div>
      </Retry>
    </WidgetCardWrapper>
  );
};

export default QoEWidget;
